// Leitura do relatorio de FX SPOT.
//
// O caminho normal de upload do Flow.Sight pede que o usuario case cada coluna
// do CSV com um campo canonico (`selectedInput.obligatory/optional`, no
// analyticsStore). Isso funciona para o relatorio de NDF, mas NAO para o de
// spot: o mapeamento posicional exige uma "Maturity Date" que simplesmente nao
// existe la, e sem ela o prazo de toda linha viraria zero.
//
// Entao o layout de spot e reconhecido pela assinatura das colunas e mapeado
// aqui, direto para a forma canonica, sem passar pelo casamento manual.
//
// Cabecalhos chegam ja normalizados por `csvToJson`: minusculas e `_` virando
// espaco. Por isso `rfq_timestamp` e comparado como `rfq timestamp`.

// Colunas que so o relatorio de spot tem. As tres juntas, porque `currency` e
// `side` existem nos dois e sozinhas nao distinguem nada.
const ASSINATURA_SPOT = ['rfq timestamp', 'amount ccy', 'spot ccybrl cost']

export const SPOT_REPORT = 'SPOT_REPORT'
export const NDF_REPORT = 'NDF_REPORT'

// Pergunta afirmativa: "este arquivo E do relatorio de spot?".
//
// Existiu antes um `detectReportLayout` que devolvia NDF como DEFAULT para tudo
// que nao casasse com a assinatura de spot. Isso transformava arquivo
// malformado em "arquivo de NDF" e - junto com a troca automatica de produto
// que existia no dataEmitter - expulsava o usuario do Flow.Sight de spot sem
// aviso. Formato desconhecido tem que dar ERRO, nao virar o outro produto.
export function isSpotLayout (fields = []) {
  const presentes = new Set((fields || []).map((f) => String(f).toLowerCase()))
  return ASSINATURA_SPOT.every((c) => presentes.has(c))
}

// `d+0` | `d+1` | `d+2` | `d+?` -> 0 | 1 | 2 | null.
// O `d+?` aparece em 867 das 4752 linhas do export de referencia, sempre em
// REJECTED/NOTH.DONE: a liquidacao nunca chegou a ser definida.
export function settlementDays (code) {
  const m = /^d\+(\d+)$/i.exec(String(code || '').trim())
  return m ? Number(m[1]) : null
}

// O relatorio de spot traz `product` por LINHA - o mesmo arquivo mistura FXSPOT
// com FXFWD (760 linhas de FXFWD no export de referencia, quase todas
// rejeitadas). Guardar isso permite separar os dois sem reprocessar o arquivo.
export function productOfRow (row) {
  const p = String(row?.product || '').trim().toUpperCase()
  if (p === 'FXSPOT' || p === 'SPOT') return 'FXSPOT'
  if (p === 'FXFWD' || p === 'FXNDF' || p === 'FWD' || p === 'NDF') return 'FXFWD'
  return p || null
}

const texto = (v) => {
  const s = String(v == null ? '' : v).trim()
  // O export usa '-' como "sem valor" em toda coluna opcional.
  return s === '' || s === '-' ? null : s
}

const numero = (v) => {
  const s = texto(v)
  if (s === null) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

const diaDe = (ts) => String(ts || '').slice(0, 10)

// Taxa CCY->BRL por (moeda, dia), a partir das linhas que TEM taxa.
//
// Sem isto o dashboard fica errado de um jeito silencioso: 3392 das 4752 linhas
// do export nao trazem `spot_ccybrl_cost` (foram rejeitadas antes de haver
// preco), e `convertNotionalToBRL` devolve 1 para linha sem taxa - ou seja,
// cada RFQ rejeitado entraria como R$ 1 no volume. Ver a memoria
// `convert-notional-to-brl.md`.
//
// A imputacao e por moeda E por dia, e cai para a ultima taxa conhecida da
// MESMA moeda quando o dia nao tem nenhuma. Nunca cruza moedas.
function buildSpotRateIndex (linhas) {
  const soma = new Map()   // `${ccy}|${dia}` -> { total, n }
  for (const r of linhas) {
    const ccy = texto(r['currency'])
    const taxa = numero(r['spot ccybrl cost'])
    if (!ccy || taxa === null || taxa <= 0) continue
    const k = `${ccy}|${diaDe(r['rfq timestamp'])}`
    const acc = soma.get(k) || { total: 0, n: 0 }
    acc.total += taxa
    acc.n += 1
    soma.set(k, acc)
  }

  const mediaPorDia = new Map()
  for (const [k, { total, n }] of soma) mediaPorDia.set(k, total / n)

  // Ordena os dias de cada moeda uma vez, para o fallback ser o dia conhecido
  // mais proximo antes (ou, na falta, o mais proximo depois).
  const diasPorMoeda = new Map()
  for (const k of mediaPorDia.keys()) {
    const [ccy, dia] = k.split('|')
    if (!diasPorMoeda.has(ccy)) diasPorMoeda.set(ccy, [])
    diasPorMoeda.get(ccy).push(dia)
  }
  for (const dias of diasPorMoeda.values()) dias.sort()

  return (ccy, dia) => {
    if (!ccy) return null
    const exata = mediaPorDia.get(`${ccy}|${dia}`)
    if (exata) return exata
    const dias = diasPorMoeda.get(ccy)
    if (!dias || !dias.length) return null
    let anterior = null
    for (const d of dias) {
      if (d <= dia) anterior = d
      else break
    }
    return mediaPorDia.get(`${ccy}|${anterior ?? dias[0]}`) || null
  }
}

// Linhas do relatorio de spot -> forma canonica que `normalizeRow` espera.
//
// Equivalencias que nao sao obvias:
//   amount ccy          -> notional
//   client name         -> counterparty
//   spot ccybrl cost    -> spotfx (taxa de custo, e o que converte para BRL)
//   spot ccybrl client  -> fwdclient / fwd, para a receita sair de client-cost
//   sett brl            -> prazo em dias (nao ha data de vencimento)
export function mapSpotReport (linhas = []) {
  const taxaDe = buildSpotRateIndex(linhas)

  return linhas.map((r) => {
    const ccy = texto(r['currency'])
    const custo = numero(r['spot ccybrl cost'])
    const cliente = numero(r['spot ccybrl client'])
    const dias = settlementDays(r['sett brl'])
    const imputada = custo === null ? taxaDe(ccy, diaDe(r['rfq timestamp'])) : null

    return {
      id: Math.random().toString(36).slice(2, 11),
      rfqtimestamp: texto(r['rfq timestamp']),
      // Sem data de vencimento no relatorio: o prazo vem da liquidacao, e
      // normalizeRow respeita `tenorDays` quando ele ja vem pronto.
      maturitydate: null,
      // Liquidacao indefinida (`d+?`) cai em 0. E uma aproximacao consciente, nao
      // uma medida: prazo de spot vai de 0 a 2 dias e as tres possibilidades
      // caem na mesma faixa de vencimento, entao a escolha nao move nenhum
      // chart. `settlement` guarda o codigo cru para quem precisar auditar.
      tenorDays: dias === null ? 0 : dias,
      settlement: texto(r['sett brl']),
      settlementCcy: texto(r['sett ccy']),

      status: texto(r['rfq status']),
      rejectedmessage: texto(r['rejected message']) || '',
      counterparty: texto(r['client name']),
      cnpj: texto(r['client cnpj']),
      channel: texto(r['rfq channel']),
      product: productOfRow(r),

      currency: ccy,
      parity: texto(r['parity ccy']),
      notional: numero(r['amount ccy']),

      // Codificacao propria deste relatorio: 'P' e venda, 'A' e compra, '-' e
      // linha rejeitada antes de haver ponta. Quem traduz e `normalizeSide`.
      side: texto(r['side']),

      spotfx: custo ?? imputada,
      // Marca a linha cuja taxa foi imputada, para nao se confundir com dado
      // observado quem for auditar o numero depois.
      spotfxImputed: custo === null && imputada !== null,

      fwd: custo,
      fwdclient: cliente,
      // Spot nao desconta: o prazo e de 0 a 2 dias.
      yieldbrlcost: 0,
    }
  })
}
